import React, { useMemo, useState } from "react";
import {
  FlipHorizontalIcon,
  FlipResetIcon,
  FlipVerticalIcon,
} from "../../../../../icons";
import CustomColorPicker from "../../../../../components/shared/CustomColorPicker";
import {
  CIRCLE_CROP_BG_OPTIONS,
  CIRCLE_CROP_BG_TYPES,
  CIRCLE_CROP_BORDER_COLOR_OPTIONS,
  CIRCLE_CROP_BORDER_MAX,
  CIRCLE_CROP_BORDER_MIN,
  CROP_ZOOM_MAX,
  CROP_ZOOM_MIN,
  clampCircleCropBorderWidth,
  clampCircleCropRotation,
  clampCropZoom,
  CIRCLE_CROP_ROTATE_MAX,
  CIRCLE_CROP_ROTATE_MIN,
  isCircleCropBorderColorCustom,
  normalizeCircleCropBackgroundType,
} from "../../../lib/circleCropPrepModel";
import "../../crop/Popup/CropPrepControls.css";
import "./CircleCropPrepControls.css";

const CircleCropPrepControls = ({
  settings,
  onChange = () => {},
  disabled = false,
  removeBgActive = false,
}) => {
  const [zoomDraft, setZoomDraft] = useState(null);
  const [rotateDraft, setRotateDraft] = useState(null);
  const [borderDraft, setBorderDraft] = useState(null);
  const [showBgPicker, setShowBgPicker] = useState(false);
  const [showBorderPicker, setShowBorderPicker] = useState(false);

  const zoom = clampCropZoom(settings?.zoom);
  const zoomInputValue = zoomDraft != null ? zoomDraft : String(zoom);
  const rotation = clampCircleCropRotation(settings?.rotation);
  const rotateInputValue = rotateDraft != null ? rotateDraft : String(rotation);
  const borderWidth = clampCircleCropBorderWidth(settings?.borderWidth);
  const borderInputValue = borderDraft != null ? borderDraft : String(borderWidth);
  const backgroundType = normalizeCircleCropBackgroundType(settings?.backgroundType);
  const bgColor = settings?.backgroundColor || "#ffffff";
  const borderColor = settings?.borderColor || "#ffffff";
  const borderCustomActive = isCircleCropBorderColorCustom(settings);
  const flipH = Boolean(settings?.flipH);
  const flipV = Boolean(settings?.flipV);

  const zoomFillPct = useMemo(() => {
    const span = CROP_ZOOM_MAX - CROP_ZOOM_MIN;
    if (!span) return 0;
    return Math.max(0, Math.min(100, ((zoom - CROP_ZOOM_MIN) / span) * 100));
  }, [zoom]);

  const rotateFillPct = useMemo(() => {
    const span = CIRCLE_CROP_ROTATE_MAX - CIRCLE_CROP_ROTATE_MIN;
    if (!span) return 0;
    return Math.max(
      0,
      Math.min(100, ((rotation - CIRCLE_CROP_ROTATE_MIN) / span) * 100)
    );
  }, [rotation]);

  const borderFillPct = useMemo(() => {
    const span = CIRCLE_CROP_BORDER_MAX - CIRCLE_CROP_BORDER_MIN;
    if (!span) return 0;
    return Math.max(
      0,
      Math.min(100, ((borderWidth - CIRCLE_CROP_BORDER_MIN) / span) * 100)
    );
  }, [borderWidth]);

  const patch = (partial) => onChange({ ...settings, ...partial });

  const commitZoomDraft = () => {
    const raw = zoomDraft != null ? zoomDraft : String(zoom);
    patch({
      zoom: clampCropZoom(raw === "" ? CROP_ZOOM_MIN : raw),
    });
    setZoomDraft(null);
  };

  const commitRotateDraft = () => {
    const raw = rotateDraft != null ? rotateDraft : String(rotation);
    patch({
      rotation: clampCircleCropRotation(
        raw === "" ? CIRCLE_CROP_ROTATE_MIN : raw
      ),
    });
    setRotateDraft(null);
  };

  const commitBorderDraft = () => {
    const raw = borderDraft != null ? borderDraft : String(borderWidth);
    patch({
      borderWidth: clampCircleCropBorderWidth(
        raw === "" ? CIRCLE_CROP_BORDER_MIN : raw
      ),
    });
    setBorderDraft(null);
  };

  return (
    <div className="crop-prep-controls ccrp-controls">
      <div className="crop-prep-controls__section">
        <div className="crop-prep-controls__slider-head">
          <span className="crop-prep-controls__section-title">Zoom</span>
          <div className="crop-prep-controls__slider-value">
            <input
              type="text"
              inputMode="numeric"
              className="crop-prep-controls__slider-input"
              value={zoomInputValue}
              disabled={disabled}
              aria-label="Zoom percentage"
              onChange={(e) => {
                setZoomDraft(e.target.value.replace(/[^0-9]/g, "").slice(0, 3));
              }}
              onBlur={commitZoomDraft}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  commitZoomDraft();
                  e.currentTarget.blur();
                }
              }}
            />
            <span className="crop-prep-controls__slider-suffix">%</span>
          </div>
        </div>
        <input
          type="range"
          className="crop-prep-controls__slider"
          min={CROP_ZOOM_MIN}
          max={CROP_ZOOM_MAX}
          step={1}
          value={zoom}
          disabled={disabled}
          aria-label="Zoom slider"
          style={{ "--crop-slider-fill": `${zoomFillPct}%` }}
          onChange={(e) => {
            setZoomDraft(null);
            patch({ zoom: clampCropZoom(e.target.value) });
          }}
        />
      </div>

      <div className="crop-prep-controls__section">
        <div className="crop-prep-controls__slider-head">
          <span className="crop-prep-controls__section-title">Rotate</span>
          <div className="crop-prep-controls__slider-value">
            <input
              type="text"
              inputMode="numeric"
              className="crop-prep-controls__slider-input"
              value={rotateInputValue}
              disabled={disabled}
              aria-label="Rotate angle"
              onChange={(e) => {
                setRotateDraft(
                  e.target.value.replace(/[^0-9]/g, "").slice(0, 3)
                );
              }}
              onBlur={commitRotateDraft}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  commitRotateDraft();
                  e.currentTarget.blur();
                }
              }}
            />
            <span className="crop-prep-controls__slider-suffix">°</span>
          </div>
        </div>
        <input
          type="range"
          className="crop-prep-controls__slider"
          min={CIRCLE_CROP_ROTATE_MIN}
          max={CIRCLE_CROP_ROTATE_MAX}
          step={1}
          value={rotation}
          disabled={disabled}
          aria-label="Rotate slider"
          style={{ "--crop-slider-fill": `${rotateFillPct}%` }}
          onChange={(e) => {
            setRotateDraft(null);
            patch({ rotation: clampCircleCropRotation(e.target.value) });
          }}
        />
      </div>

      <div className="crop-prep-controls__section">
        <span className="crop-prep-controls__section-title">Flip</span>
        <div className="ccrp-flip-row" role="group" aria-label="Flip">
          <button
            type="button"
            className="ccrp-flip-btn"
            disabled={disabled}
            aria-label="Reset flip"
            onClick={() => patch({ flipH: false, flipV: false })}
          >
            <FlipResetIcon className="ccrp-flip-btn__icon" />
            <span className="ccrp-flip-btn__label">Reset</span>
          </button>
          <button
            type="button"
            className={`ccrp-flip-btn${flipH ? " is-active" : ""}`}
            disabled={disabled}
            aria-label="Flip horizontal"
            aria-pressed={flipH}
            onClick={() => patch({ flipH: !flipH })}
          >
            <FlipHorizontalIcon className="ccrp-flip-btn__icon" />
            <span className="ccrp-flip-btn__label">Flip H</span>
          </button>
          <button
            type="button"
            className={`ccrp-flip-btn${flipV ? " is-active" : ""}`}
            disabled={disabled}
            aria-label="Flip vertical"
            aria-pressed={flipV}
            onClick={() => patch({ flipV: !flipV })}
          >
            <FlipVerticalIcon className="ccrp-flip-btn__icon" />
            <span className="ccrp-flip-btn__label">Flip V</span>
          </button>
        </div>
      </div>

      {removeBgActive ? (
        <div className="crop-prep-controls__section">
          <span className="crop-prep-controls__section-title">Background</span>
          <div className="ccrp-swatches" role="group" aria-label="Background">
            {CIRCLE_CROP_BG_OPTIONS.map((opt) => {
              const active = backgroundType === opt.id;
              const isCustom = opt.id === CIRCLE_CROP_BG_TYPES.CUSTOM;
              const isTransparent = opt.id === CIRCLE_CROP_BG_TYPES.TRANSPARENT;
              const isBlur = opt.id === CIRCLE_CROP_BG_TYPES.BLUR;
              const isMulti = opt.id === CIRCLE_CROP_BG_TYPES.MULTICOLOR;
              const isWhite = opt.id === CIRCLE_CROP_BG_TYPES.WHITE;
              const isBlack = opt.id === CIRCLE_CROP_BG_TYPES.BLACK;

              return (
                <button
                  key={opt.id}
                  type="button"
                  className={`ccrp-swatch${
                    active ? " is-active" : ""
                  }${isTransparent ? " ccrp-swatch--checker" : ""}${
                    isBlur ? " ccrp-swatch--blur" : ""
                  }${isMulti ? " ccrp-swatch--multicolor" : ""}${
                    isCustom ? " ccrp-swatch--picker" : ""
                  }`}
                  style={
                    isWhite
                      ? { backgroundColor: "#ffffff" }
                      : isBlack
                        ? { backgroundColor: "#000000" }
                        : isCustom && backgroundType === CIRCLE_CROP_BG_TYPES.CUSTOM
                          ? { backgroundColor: bgColor }
                          : undefined
                  }
                  disabled={disabled}
                  aria-label={opt.label}
                  aria-pressed={active}
                  onClick={() => {
                    if (isCustom) {
                      setShowBgPicker(true);
                      patch({ backgroundType: CIRCLE_CROP_BG_TYPES.CUSTOM });
                      return;
                    }
                    patch({ backgroundType: opt.id });
                  }}
                />
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="crop-prep-controls__section">
        <div className="crop-prep-controls__slider-head">
          <span className="crop-prep-controls__section-title">Border</span>
          <div className="crop-prep-controls__slider-value">
            <input
              type="text"
              inputMode="numeric"
              className="crop-prep-controls__slider-input"
              value={borderInputValue}
              disabled={disabled}
              aria-label="Border width percentage"
              onChange={(e) => {
                setBorderDraft(
                  e.target.value.replace(/[^0-9]/g, "").slice(0, 2)
                );
              }}
              onBlur={commitBorderDraft}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  commitBorderDraft();
                  e.currentTarget.blur();
                }
              }}
            />
            <span className="crop-prep-controls__slider-suffix">%</span>
          </div>
        </div>
        <input
          type="range"
          className="crop-prep-controls__slider"
          min={CIRCLE_CROP_BORDER_MIN}
          max={CIRCLE_CROP_BORDER_MAX}
          step={1}
          value={borderWidth}
          disabled={disabled}
          aria-label="Border width slider"
          style={{ "--crop-slider-fill": `${borderFillPct}%` }}
          onChange={(e) => {
            setBorderDraft(null);
            patch({
              borderWidth: clampCircleCropBorderWidth(e.target.value),
            });
          }}
        />
      </div>

      <div className="crop-prep-controls__section">
        <span className="crop-prep-controls__section-title">Border color</span>
        <div className="ccrp-swatches" role="group" aria-label="Border color">
          {CIRCLE_CROP_BORDER_COLOR_OPTIONS.map((opt) => {
            const active =
              !borderCustomActive &&
              String(borderColor).toLowerCase() === opt.color.toLowerCase();
            return (
              <button
                key={opt.id}
                type="button"
                className={`ccrp-swatch${active ? " is-active" : ""}`}
                style={{ backgroundColor: opt.color }}
                disabled={disabled}
                aria-label={opt.label}
                aria-pressed={active}
                onClick={() => patch({ borderColor: opt.color })}
              />
            );
          })}
          <button
            type="button"
            className={`ccrp-swatch ccrp-swatch--picker${
              borderCustomActive ? " is-active" : ""
            }`}
            style={
              borderCustomActive
                ? { backgroundColor: settings?.borderColorCustom || "#2563eb" }
                : undefined
            }
            disabled={disabled}
            aria-label="Custom border color"
            aria-pressed={borderCustomActive}
            onClick={() => setShowBorderPicker(true)}
          />
        </div>
      </div>

      {removeBgActive ? (
        <CustomColorPicker
          isOpen={showBgPicker}
          onClose={() => setShowBgPicker(false)}
          color={bgColor}
          onChange={(color) =>
            patch({
              backgroundType: CIRCLE_CROP_BG_TYPES.CUSTOM,
              backgroundColor: color,
            })
          }
          onApply={(color) => {
            patch({
              backgroundType: CIRCLE_CROP_BG_TYPES.CUSTOM,
              backgroundColor: color,
            });
            setShowBgPicker(false);
          }}
          anchorSelector='.tool-prep-popup[data-tool="circle_crop"] .tool-prep-popup__left'
          anchorGap={10}
        />
      ) : null}

      <CustomColorPicker
        isOpen={showBorderPicker}
        onClose={() => setShowBorderPicker(false)}
        color={settings?.borderColorCustom || "#2563eb"}
        onChange={(color) =>
          patch({
            borderColor: "custom",
            borderColorCustom: color,
          })
        }
        onApply={(color) => {
          patch({
            borderColor: "custom",
            borderColorCustom: color,
          });
          setShowBorderPicker(false);
        }}
        anchorSelector='.tool-prep-popup[data-tool="circle_crop"] .tool-prep-popup__left'
        anchorGap={10}
      />
    </div>
  );
};

export default CircleCropPrepControls;
